import { useState, useEffect, useCallback } from "react";
import FrameConfig from "@lib/classes/FrameConfig";

interface FrameConfigState {
  bridgeId?: number;
  bridgeType?: number;
  language?: string;
  override?: string;
  jsonHeaders?: Record<string, string>;
}

/**
 * Custom hook to manage FrameConfig state and listen for changes.
 *
 * Returns the current language, override, and jsonHeaders from FrameConfig,
 * and automatically updates when sessionStorage changes (e.g., from another component
 * or tab within the same browser tab).
 */
export function useFrameConfig(): FrameConfigState {
  const [state, setState] = useState<FrameConfigState>({});

  const updateFromFrameConfig = useCallback(() => {
    const frameConfig = new FrameConfig();
    setState((prev) => {
      // Only update if values actually changed to prevent unnecessary re-renders
      const newJsonHeadersStr = JSON.stringify(frameConfig.jsonHeaders);
      const prevJsonHeadersStr = JSON.stringify(prev.jsonHeaders);

      if (
        prev.bridgeId === frameConfig.bridgeId &&
        prev.bridgeType === frameConfig.bridgeType &&
        prev.language === frameConfig.language &&
        prev.override === frameConfig.override &&
        prevJsonHeadersStr === newJsonHeadersStr
      ) {
        return prev; // No change, return same reference
      }

      return {
        bridgeId: frameConfig.bridgeId,
        bridgeType: frameConfig.bridgeType,
        language: frameConfig.language,
        override: frameConfig.override,
        jsonHeaders: frameConfig.jsonHeaders,
      };
    });
  }, []);

  useEffect(() => {
    // Handle storage events - only react to our frameConfig key (cross-tab changes)
    const handleStorageChange = (event: StorageEvent) => {
      // Only update if the change is to our frameConfig key in sessionStorage
      const frameConfigKey = `${window.siteId}.frameConfig`;
      if (event.storageArea === sessionStorage && event.key === frameConfigKey) {
        updateFromFrameConfig();
      }
    };

    // Get initial values
    updateFromFrameConfig();

    // Listen for storage changes (cross-tab) and frameConfigChange (same-tab)
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("frameConfigChange", updateFromFrameConfig);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("frameConfigChange", updateFromFrameConfig);
    };
  }, []);

  return state;
}